import type { Product } from "@/types"

type AvailabilityFields = {
  available?: boolean
  saleable?: boolean
  availability?: string | boolean | null
}

const UNAVAILABLE_VALUES = new Set(["false", "outofstock", "out_of_stock", "out-of-stock", "oos", "notavailable"])

/**
 * Mirrors `nosto-dickies-product-card`, which only emits markup for
 * `product.available`. Nosto may still return unavailable hits, so remove those
 * before style-family dedupe and pagination.
 *
 * Also filters hits that lack a usable `handle` (derived from `product.url` by
 * `handleDecorator`). `DynamicCardProduct` returns `null` for handle-less hits
 * because the Shopify section URL cannot be built. Without this filter, those
 * hits consume a grid slot — and a `dedupeByStyleFamily` family slot — but
 * render nothing, producing visible gaps on the first page.
 */
export function isRenderableProductHit(product: Product): boolean {
  const { available, saleable, availability } = product as Product & AvailabilityFields

  if (typeof product.handle !== "string" || product.handle.length === 0) {
    return false
  }

  if (available === false || saleable === false) {
    return false
  }

  if (typeof availability === "boolean") {
    return availability
  }

  if (typeof availability === "string") {
    const normalized = availability.toLowerCase().replace(/[\s_-]/g, "")
    return !UNAVAILABLE_VALUES.has(normalized)
  }

  return true
}
